import { z } from "zod";
import { planRowSchema, requestListSchema, requestSummarySchema, stateSchema } from "./contracts.js";

const tokenSchema = z.string().min(40).max(20_000);
const nullableText = z.string().trim().min(1).max(500).nullable();

export const labelSourceIssueSchema = z.object({
  code: z.enum([
    "PRODUCT_MISSING", "CUSTOMER_MISSING", "ORDER_NUMBER_MISSING", "ORDER_LINE_MISSING",
    "UNIT_MISSING", "LOT_MISSING", "TRACE_CONFLICT", "SOURCE_FIELD_MISSING",
  ]),
  field: z.string().min(1).max(100),
  severity: z.enum(["blocking", "warning"]),
  message: z.string().min(1).max(500),
}).strict();

export const labelWorkOrderSelectionSchema = z.object({
  selectionToken: tokenSchema,
  workOrder: z.string().min(1).max(80),
  productCode: nullableText,
  productName: nullableText,
  customerCode: nullableText,
  customerName: nullableText,
  orderNumber: nullableText,
  orderLine: z.number().int().nonnegative().nullable(),
  lot: nullableText,
  lineName: nullableText,
  unit: nullableText,
  plannedQuantity: z.number().finite().nonnegative().nullable(),
  issues: z.array(labelSourceIssueSchema),
}).strict();
export type LabelWorkOrderSelection = z.infer<typeof labelWorkOrderSelectionSchema>;

export const labelWorkOrderLookupRequestSchema = z.object({
  workOrder: z.string().trim().min(1).max(80)
    .regex(/^[\p{L}\p{N} ._/-]+$/u),
}).strict();
export const labelWorkOrderLookupResponseSchema = z.object({
  lookupToken: tokenSchema,
  workOrder: z.string().min(1).max(80),
  expiresAt: z.string().datetime(),
  options: z.array(labelWorkOrderSelectionSchema).max(20),
}).strict();
export type LabelWorkOrderLookupResponse = z.infer<typeof labelWorkOrderLookupResponseSchema>;

export const labelWorkOrderFieldsSchema = z.object({
  variantKey: z.string().trim().min(1).max(160),
  labelKind: z.string().trim().min(1).max(200),
  packaging: z.string().trim().min(1).max(1000),
  customerRequest: z.string().trim().max(10_000).default(""),
  language: z.string().trim().min(1).max(160),
  requestedPrintQuantity: z.number().int().positive().max(100_000_000),
  dueAt: z.string().datetime().nullable().default(null),
}).strict();
export type LabelWorkOrderFields = z.infer<typeof labelWorkOrderFieldsSchema>;

export const labelExistingRequestFieldsSchema = z.object({
  variantKey: z.string(), labelKind: z.string(), packaging: z.string(),
  language: z.string().trim().min(1).max(160).nullable(),
  requestedPrintQuantity: z.number().int().positive().nullable(),
}).strict();
export const labelCandidateSourceSchema = z.object({
  workOrder: z.string().min(1), productCode: nullableText, customerCode: nullableText, orderNumber: nullableText,
  orderLine: z.number().int().nonnegative().nullable(), lot: nullableText, lineName: nullableText,
}).strict();
export const labelSourceMatchSchema = z.object({
  kind: z.enum(["exact", "partial", "conflict"]), matchedFields: z.array(z.string()),
  missingFields: z.array(z.string()), conflictingFields: z.array(z.string()),
}).strict();
export const labelExistingRequestCandidateSchema = z.object({
  candidateToken: tokenSchema,
  request: requestSummarySchema,
  labelFields: labelExistingRequestFieldsSchema,
  source: labelCandidateSourceSchema,
  match: labelSourceMatchSchema,
}).strict();
export const labelWorkOrderLookupReviewSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }).strict(),
  z.object({ kind: z.literal("exact"), candidate: labelExistingRequestCandidateSchema }).strict(),
  z.object({ kind: z.literal("multiple"), candidates: z.array(labelExistingRequestCandidateSchema).min(2).max(20) }).strict(),
]);
export const labelWorkOrderReviewRequestSchema = z.object({
  lookupToken: tokenSchema,
  selectionToken: tokenSchema,
  labelFields: labelWorkOrderFieldsSchema,
}).strict();
export const labelWorkOrderReviewResponseSchema = z.object({
  reviewToken: tokenSchema,
  expiresAt: z.string().datetime(),
  selection: labelWorkOrderSelectionSchema,
  labelFields: labelWorkOrderFieldsSchema,
  blockers: z.array(labelSourceIssueSchema),
  existing: labelWorkOrderLookupReviewSchema,
}).strict();
export type LabelWorkOrderReviewResponse = z.infer<typeof labelWorkOrderReviewResponseSchema>;

export const labelWorkOrderCreateResolutionSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("create") }).strict(),
  z.object({ kind: z.literal("link_existing"), candidateToken: tokenSchema }).strict(),
  z.object({ kind: z.literal("create_variant"), variantKey: z.string().trim().min(1).max(160), reason: z.string().trim().min(3).max(2000) }).strict(),
]);
export const labelWorkOrderCreateRequestSchema = z.object({
  reviewToken: tokenSchema,
  resolution: labelWorkOrderCreateResolutionSchema,
}).strict();
export const labelWorkOrderCreateResultSchema = z.object({
  outcome: z.enum(["created", "linked_existing", "idempotent_replay"]),
  request: requestSummarySchema,
}).strict();
export type LabelWorkOrderCreateResult = z.infer<typeof labelWorkOrderCreateResultSchema>;

export const labelPlanSourceListQuerySchema = z.object({
  weekYear: z.coerce.number().int().min(2020).max(2100).optional(),
  weekNumber: z.coerce.number().int().min(1).max(53).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(20),
}).strict();
export const labelPlanSourceSummarySchema = z.object({
  id: z.string().uuid(), fileName: z.string(), sha256: z.string().regex(/^[a-f0-9]{64}$/u), sheetName: z.string(),
  importedAt: z.string().datetime(), weekYear: z.number().int().nullable(), weekNumber: z.number().int().nullable(),
  rowCount: z.number().int().nonnegative(),
}).strict();
export const labelPlanSourceListResponseSchema = z.object({
  items: z.array(labelPlanSourceSummarySchema), total: z.number().int().nonnegative(),
  page: z.number().int().positive(), pageSize: z.number().int().positive(),
}).strict();
export type LabelPlanSourceListResponse = z.infer<typeof labelPlanSourceListResponseSchema>;
export const labelPlanSourceCandidateSchema = labelExistingRequestCandidateSchema;
export const labelPlanSourceLinkSchema = z.object({
  requestId: z.string().uuid(), requestNo: z.string(), state: stateSchema, variantKey: z.string(), linkedAt: z.string().datetime(),
}).strict();
export const labelPlanSourceDetailSchema = z.object({
  source: labelPlanSourceSummarySchema.extend({ warnings: z.array(z.string()) }),
  rows: z.array(z.object({
    row: planRowSchema, issues: z.array(labelSourceIssueSchema),
    links: z.array(labelPlanSourceLinkSchema), candidates: z.array(labelPlanSourceCandidateSchema),
  }).strict()),
}).strict();
export type LabelPlanSourceDetail = z.infer<typeof labelPlanSourceDetailSchema>;

export const labelPlanSourceLinkRequestSchema = z.object({
  requestId: z.string().uuid(), variantKey: z.string().trim().min(1).max(160), matchToken: tokenSchema,
  confirmed: z.literal(true), reason: z.string().trim().min(3).max(2000),
}).strict();
export const labelPlanSourceLinkResultSchema = z.object({
  outcome: z.enum(["linked", "idempotent_replay"]), requestId: z.string().uuid(),
  sourceRowId: z.string().uuid(), variantKey: z.string(),
}).strict();
export type LabelPlanSourceLinkResult = z.infer<typeof labelPlanSourceLinkResultSchema>;

export const labelWorkOrderMineResponseSchema = requestListSchema;

export const labelPreparedPlanSourceKeySchema = z.literal("2026-week-45");
export const labelPreparedPlanSourceCatalogSchema = z.object({
  items: z.array(z.object({
    key: labelPreparedPlanSourceKeySchema,
    label: z.string().min(1), weekYear: z.literal(2026), weekNumber: z.literal(45), sheetName: z.literal("45. Hafta"),
    sha256: z.string().regex(/^[a-f0-9]{64}$/u), fileName: z.string().min(1), rows: z.array(planRowSchema),
    candidateCount: z.number().int().nonnegative(), requiredCount: z.number().int().nonnegative(),
    verifyCount: z.number().int().nonnegative(), blockingRows: z.array(z.number().int().positive()),
    issues: z.array(z.union([z.string(), z.object({ message: z.string().min(1) }).passthrough()])),
    savedSourceId: z.string().uuid().nullable(),
  }).strict()).max(1),
}).strict();
export const labelPreparedPlanSourceSaveRequestSchema = z.object({ key: labelPreparedPlanSourceKeySchema }).strict();
